import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getLocalStorage } from '@libs/storage';

import { getPlatform, Platform } from '@features/video/lib/permissionsHelp';
import { RNNOISE_SUPPORTED } from '@features/video/lib/rnnoise';
import { MediaDeviceOption } from '@features/video/types';

// Выбранные устройства общие для всех страниц
const DEVICES_KEY = 'video:devices';

// Ошибка одного устройства: нет, запрещено сайту, запрещено браузеру в системе,
// занято другим приложением (на Windows так же выглядит системный запрет); silent — только
// микрофон: захват есть, но звук страницы не запускается и после нажатия (Firefox на Android,
// решение 31). silent не мешает входу: статус остается ready
export type DeviceErrorKind = 'missing' | 'denied' | 'system' | 'busy' | 'silent';

// Ошибка всего сразу: браузер без медиа или что-то непредвиденное
export type MediaError = { kind: 'unsupported' } | { kind: 'unknown'; message: string };

export type MediaDevicesStatus = 'idle' | 'requesting' | 'ready' | 'error';

export type MediaDevices = {
  status: MediaDevicesStatus;
  // живой поток с выбранными устройствами; живет, пока смонтирован хук
  stream: MediaStream | null;
  cams: MediaDeviceOption[];
  mics: MediaDeviceOption[];
  camId: string | null;
  micId: string | null;
  // ошибки устройств независимы: камера может работать при занятом микрофоне
  camError: DeviceErrorKind | null;
  micError: DeviceErrorKind | null;
  error: MediaError | null;
  // от платформы зависят тексты и инструкции системных блокировок
  platform: Platform;
  setCamId: (deviceId: string) => void;
  setMicId: (deviceId: string) => void;
  retry: () => void;
};

export type MediaDevicesOptions = {
  video?: boolean;
  audio?: boolean;
  // false — устройства не запрашиваем и отпускаем
  enabled?: boolean;
};

type Errors = Pick<MediaDevices, 'camError' | 'micError' | 'error'>;

const NO_ERRORS: Errors = { camError: null, micError: null, error: null };

const PLATFORM = getPlatform();

// Сколько ждем запуска пробного аудиоконтекста: сразу после захвата и после нажатия
const PROBE_WAIT_MS = 1000;

// Камера и микрофон браузера: запрос доступа, списки устройств, смена устройства,
// все ошибки доступа. Поток принадлежит тому, кто вызвал хук
export function useMediaDevices({
  video = false,
  audio = true,
  enabled = true,
}: MediaDevicesOptions = {}): MediaDevices {
  const [status, setStatus] = useState<MediaDevicesStatus>('idle');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [lists, setLists] = useState<{ cams: MediaDeviceOption[]; mics: MediaDeviceOption[] }>({
    cams: [],
    mics: [],
  });
  // Текущие устройства: выбранные пользователем или выданные браузером, если выбранных не
  // оказалось. Запрос читает их через ref: узнав выданные, мы меняем ids, но захват не
  // повторяем — устройства уже в руках, а повторный захват сразу после отпускания на Android
  // (Firefox) дает молчащий микрофон, и Firefox спрашивает разрешение на каждый запрос
  const [ids, setIds] = useState(readIds);
  const refIds = useRef(ids);
  const [errors, setErrors] = useState<Errors>(NO_ERRORS);
  // каждый запрос устройств: монтирование, выбор устройства, повтор, возврат устройств
  const [attempt, setAttempt] = useState(0);

  const refStream = useRef<MediaStream | null>(null);
  const refStatus = useRef(status);
  refStatus.current = status;
  const stop = useCallback(() => {
    refStream.current?.getTracks().forEach((t) => t.stop());
    refStream.current = null;
    setStream(null);
  }, []);

  // Запрос устройств: при монтировании и на каждый attempt (смена устройства, повтор, возврат
  // устройств). Выданные устройства в зависимостях не участвуют, см. ids
  useEffect(() => {
    if (!enabled || (!video && !audio)) {
      stop();
      setStatus('idle');
      return;
    }
    let cancelled = false;
    setStatus('requesting');
    setErrors(NO_ERRORS);

    (async () => {
      stop();
      const requested = refIds.current;
      const next = await acquire({ video, audio, ...requested });
      if (cancelled) {
        next.stream?.getTracks().forEach((t) => t.stop());
        return;
      }
      refStream.current = next.stream;
      setStream(next.stream);
      setErrors(next);
      if (next.stream) {
        setLists(await listDevices());
        // фактически выданные устройства: выбранного могло не оказаться
        const actual = {
          camId: next.stream.getVideoTracks()[0]?.getSettings().deviceId ?? requested.camId,
          micId: next.stream.getAudioTracks()[0]?.getSettings().deviceId ?? requested.micId,
        };
        refIds.current = actual;
        setIds(actual);
        // выбранное устройство пропало — запоминаем выданное, чтобы в следующий раз
        // не спотыкаться об OverconstrainedError
        if (
          (requested.camId && actual.camId !== requested.camId) ||
          (requested.micId && actual.micId !== requested.micId)
        ) {
          writeIds(actual);
        }
      }
      setStatus(next.camError || next.micError || next.error ? 'error' : 'ready');
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, video, audio, attempt, stop]);

  // Уход со страницы: отпускаем устройства
  useEffect(() => stop, [stop]);

  // Проба звука на свежем захвате (решение 31): в Firefox на Android захват иногда стартует
  // так, что ни один аудиоконтекст страницы не запускается, даже после нажатия, и микрофон
  // молчит — и в индикаторе, и в эфире. Лечит только новый захват. Контекст запустился сам
  // или по первому нажатию — все в порядке. Не запустился и в нажатии — один раз берем захват
  // заново сами (Firefox спросит разрешение), не помогло — ошибка silent с кнопкой
  const refAutoRetried = useRef(false);
  useEffect(() => {
    const track = stream?.getAudioTracks()[0];
    if (!track || typeof AudioContext === 'undefined') return;
    let ctx: AudioContext;
    try {
      ctx = new AudioContext();
    } catch {
      return;
    }
    let done = false;
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    const running = () =>
      Promise.race([
        ctx.resume().then(
          () => ctx.state === 'running',
          () => false,
        ),
        new Promise<boolean>((r) => setTimeout(() => r(ctx.state === 'running'), PROBE_WAIT_MS)),
      ]);
    const unlisten = () =>
      GESTURES.forEach((g) => document.removeEventListener(g, onGesture, true));
    const verdict = (healthy: boolean) => {
      if (done) return;
      done = true;
      unlisten();
      if (healthy) {
        refAutoRetried.current = false;
      } else if (!refAutoRetried.current) {
        refAutoRetried.current = true;
        setAttempt((n) => n + 1);
      } else {
        setErrors((prev) => ({ ...prev, micError: 'silent' }));
      }
    };
    // нажатие: resume() синхронно в жесте, затем ждем запуска
    function onGesture() {
      unlisten();
      running().then(verdict);
    }
    running().then((ok) => {
      if (done) return;
      if (ok) verdict(true);
      else GESTURES.forEach((g) => document.addEventListener(g, onGesture, true));
    });
    return () => {
      done = true;
      unlisten();
      source.disconnect();
      ctx.close().catch(() => {});
    };
  }, [stream]);

  // Пользователь поменял разрешение в настройках браузера: после запрета сразу
  // показываем ошибку, после разрешения сами запрашиваем заново
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const statuses: PermissionStatus[] = [];
    const names = [video && 'camera', audio && 'microphone'].filter((n) => n !== false);
    Promise.all(names.map(queryPermission)).then((list) => {
      if (!active) return;
      list.forEach((st) => {
        if (!st) return;
        st.onchange = () => {
          if (st.state !== 'granted' || refStatus.current === 'error') setAttempt((n) => n + 1);
        };
        statuses.push(st);
      });
    });
    return () => {
      active = false;
      statuses.forEach((st) => {
        st.onchange = null;
      });
    };
  }, [enabled, video, audio]);

  // Подключили или отключили устройство: обновляем списки и перезапрашиваем, если нужно
  useEffect(() => {
    const media = navigator.mediaDevices;
    if (!enabled || !media?.addEventListener) return;
    const onChange = async () => {
      const next = await listDevices();
      setLists(next);
      const lost =
        (ids.camId && !next.cams.some((d) => d.deviceId === ids.camId)) ||
        (ids.micId && !next.mics.some((d) => d.deviceId === ids.micId));
      if (lost || status === 'error') setAttempt((n) => n + 1);
    };
    media.addEventListener('devicechange', onChange);
    return () => media.removeEventListener('devicechange', onChange);
  }, [enabled, ids.camId, ids.micId, status]);

  // Выбор устройства: то же устройство — ничего не делаем, иначе новый захват
  const select = useCallback((patch: Partial<DeviceIds>) => {
    const next = { ...refIds.current, ...patch };
    if (next.camId === refIds.current.camId && next.micId === refIds.current.micId) return;
    refIds.current = next;
    writeIds(next);
    setIds(next);
    setAttempt((n) => n + 1);
  }, []);

  return useMemo(
    () => ({
      status,
      stream,
      cams: lists.cams,
      mics: lists.mics,
      camId: ids.camId,
      micId: ids.micId,
      ...errors,
      platform: PLATFORM,
      setCamId: (camId: string) => select({ camId }),
      setMicId: (micId: string) => select({ micId }),
      retry: () => setAttempt((n) => n + 1),
    }),
    [status, stream, lists, ids, errors, select],
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

type DeviceIds = { camId: string | null; micId: string | null };

// Действия пользователя, в которых браузер разрешает запустить звук
const GESTURES = ['pointerdown', 'keydown', 'click'] as const;

type AcquireResult = Errors & { stream: MediaStream | null };

async function acquire({
  video,
  audio,
  camId,
  micId,
}: DeviceIds & { video: boolean; audio: boolean }): Promise<AcquireResult> {
  const media = navigator.mediaDevices;
  if (!media?.getUserMedia) return { ...NO_ERRORS, stream: null, error: { kind: 'unsupported' } };

  const result: AcquireResult = { ...NO_ERRORS, stream: null };

  // Сначала смотрим, какие устройства вообще есть: отсутствующее не просим,
  // а остальные запрашиваем как обычно
  try {
    const all = await media.enumerateDevices();
    if (video && !all.some((d) => d.kind === 'videoinput')) {
      result.camError = 'missing';
      video = false;
    }
    if (audio && !all.some((d) => d.kind === 'audioinput')) {
      result.micError = 'missing';
      audio = false;
    }
  } catch {
    // списка нет — узнаем по ошибке запроса
  }
  // Запрет уже известен браузеру: не дергаем запрос, сразу пишем ошибку
  const [camDenied, micDenied] = await Promise.all([
    video ? isPermissionDenied('camera') : false,
    audio ? isPermissionDenied('microphone') : false,
  ]);
  if (camDenied) {
    result.camError = 'denied';
    video = false;
  }
  if (micDenied) {
    result.micError = 'denied';
    audio = false;
  }
  if (!video && !audio) return result;

  const constraints = (exact: boolean, v = video, a = audio): MediaStreamConstraints => ({
    audio: a && {
      deviceId: exact && micId ? { exact: micId } : undefined,
      echoCancellation: true,
      // браузерное шумоподавление только там, где нет RNNoise: иначе двойная обработка
      noiseSuppression: !RNNOISE_SUPPORTED,
      autoGainControl: true,
    },
    video: v && {
      deviceId: exact && camId ? { exact: camId } : undefined,
      // на телефоне по умолчанию задняя камера: ей снимают доску
      facingMode: exact && camId ? undefined : 'environment',
      // 720p при 15 кадрах: судье важна резкость, а не плавность; кодирует телефон
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 15 },
    },
  });

  // Отказ: выясняем, по какому устройству, пробуя каждое отдельно
  const failed = async (e: unknown): Promise<AcquireResult> => {
    const kind = toDeviceError(e);
    if (!kind) return { ...result, error: toMediaError(e) };
    if (video && audio) {
      const audioOnly = await tryGetUserMedia(constraints(false, false, true));
      if (audioOnly) return { ...result, stream: audioOnly, camError: kind };
      const videoOnly = await tryGetUserMedia(constraints(false, true, false));
      if (videoOnly) return { ...result, stream: videoOnly, micError: kind };
      return { ...result, camError: kind, micError: kind };
    }
    return video ? { ...result, camError: kind } : { ...result, micError: kind };
  };

  try {
    return { ...result, stream: await media.getUserMedia(constraints(true)) };
  } catch (e) {
    // выбранного устройства больше нет — берем любое
    if (e instanceof DOMException && e.name === 'OverconstrainedError') {
      try {
        return { ...result, stream: await media.getUserMedia(constraints(false)) };
      } catch (e2) {
        return failed(e2);
      }
    }
    return failed(e);
  }
}

// Состояние разрешения без запроса; старые браузеры не знают имен camera и microphone
async function queryPermission(name: string): Promise<PermissionStatus | null> {
  try {
    return await navigator.permissions.query({ name: name as PermissionName });
  } catch {
    return null;
  }
}

async function isPermissionDenied(name: string): Promise<boolean> {
  return (await queryPermission(name))?.state === 'denied';
}

async function tryGetUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream | null> {
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch {
    return null;
  }
}

function toDeviceError(e: unknown): DeviceErrorKind | null {
  if (!(e instanceof DOMException)) return null;
  // Chrome на macOS: браузер заблокирован в настройках системы
  if (e.name === 'NotAllowedError' && /by system/i.test(e.message)) return 'system';
  if (e.name === 'NotAllowedError' || e.name === 'SecurityError') return 'denied';
  if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') return 'missing';
  if (e.name === 'NotReadableError' || e.name === 'AbortError') return 'busy';
  return null;
}

function toMediaError(e: unknown): MediaError {
  return { kind: 'unknown', message: e instanceof Error ? e.message : String(e) };
}

async function listDevices(): Promise<{ cams: MediaDeviceOption[]; mics: MediaDeviceOption[] }> {
  const all = await navigator.mediaDevices.enumerateDevices();
  const pick = (kind: MediaDeviceKind, fallback: string) =>
    all
      .filter((d) => d.kind === kind && d.deviceId)
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `${fallback} ${i + 1}` }));
  return { cams: pick('videoinput', 'Камера'), mics: pick('audioinput', 'Микрофон') };
}

function readIds(): DeviceIds {
  try {
    const raw = getLocalStorage().getItem(DEVICES_KEY);
    return raw ? { camId: null, micId: null, ...JSON.parse(raw) } : { camId: null, micId: null };
  } catch {
    return { camId: null, micId: null };
  }
}

function writeIds(ids: DeviceIds): void {
  getLocalStorage().setItem(DEVICES_KEY, JSON.stringify(ids));
}
